import { useEffect, useState } from "react";
import { Users, Sunrise, CalendarCheck, Clock, CalendarPlus } from "lucide-react";
import api from "@/lib/api";

/**
 * TodayOverview — trimmed down to just the 5 quick-glance mini-stats.
 *
 * Managers use these numbers every day. The old sparkline / staffing-levels /
 * who's-off-today panels were removed after user feedback that they were
 * clutter; the same information is now available under the Team Calendar,
 * Month Calendar, Heatmap and Bank Holidays tabs.
 *
 * The staffing-levels API endpoint is intentionally kept — it's still used by
 * the Heatmap page and future planning views.
 */
export default function TodayOverview({ refreshKey }) {
  const [today, setToday] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/dashboard/today")
      .then(({ data }) => { if (!cancelled) setToday(data); })
      .catch(() => { if (!cancelled) setToday(null); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="today-widgets">
      <MiniStat
        testid="today-off-count"
        label="Off today"
        value={today?.off_today?.length ?? 0}
        icon={<Users className="w-3.5 h-3.5" />}
      />
      <MiniStat
        testid="today-tomorrow-count"
        label="Off tomorrow"
        value={today?.off_tomorrow?.length ?? 0}
        icon={<Sunrise className="w-3.5 h-3.5" />}
      />
      <MiniStat
        testid="today-week-count"
        label="Off this week"
        value={today?.off_this_week?.length ?? 0}
        icon={<CalendarCheck className="w-3.5 h-3.5" />}
      />
      <MiniStat
        testid="today-pending-count"
        label="Pending approvals"
        value={today?.pending_count ?? 0}
        tone={today?.pending_count > 0 ? "pending" : ""}
        icon={<Clock className="w-3.5 h-3.5" />}
      />
      <MiniStat
        testid="today-upcoming-count"
        label="Upcoming approved"
        value={today?.upcoming_count ?? 0}
        icon={<CalendarPlus className="w-3.5 h-3.5" />}
      />
    </div>
  );
}

function MiniStat({ testid, label, value, tone, icon }) {
  return (
    <div className={`rounded-lg border p-3 min-w-0 ${tone ? `status-${tone}` : "bg-[#1E1E1E] border-border"}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.08em] font-semibold opacity-80 flex items-center gap-1 truncate">
        {icon}{label}
      </div>
      <div className="font-heading text-2xl font-semibold mt-0.5 tabular-nums">{value}</div>
    </div>
  );
}
